import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useMediaDevices } from '../hooks/useMediaDevices';
import { useMeeting } from '../hooks/useMeeting';
import { WebRTCManager, AudioLevelMonitor } from '../lib/webrtc';
import { Lobby } from '../components/Lobby';
import { VideoGrid } from '../components/VideoGrid';
import { ControlBar } from '../components/ControlBar';
import { ParticipantList } from '../components/ParticipantList';
import { ChatPanel } from '../components/ChatPanel';
import { FileShareModal } from '../components/FileShareModal';
import { DeviceSelector } from '../components/DeviceSelector';
import { ReconnectingBanner } from '../components/ReconnectingBanner';
import { LayoutMode, Participant } from '../types';
import { api } from '../lib/api';
import { SelfVideoPopup } from '../components/SelfVideoPopup';
import { Video, PhoneOff } from 'lucide-react';

interface MeetingPageProps {
  publicId: string;
}

export const MeetingPage: React.FC<MeetingPageProps> = ({ publicId }) => {
  const { user, setUser } = useAuth();
  const [joined, setJoined] = useState(false);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid');
  const [pinnedId, setPinnedId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<'participants' | 'chat' | 'files' | null>(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map());
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const devices = useMediaDevices();
  const webrtcRef = useRef<WebRTCManager | null>(null);
  const audioMonitorRef = useRef<AudioLevelMonitor | null>(null);

  const handleRemoteTrack = useCallback((participantId: string, stream: MediaStream) => {
    setRemoteStreams((prev) => {
      const next = new Map(prev);
      next.set(participantId, stream);
      return next;
    });
  }, []);

  const handleSignal = useCallback((fromParticipantId: string, signalData: any) => {
    webrtcRef.current?.handleSignal(fromParticipantId, signalData);
  }, []);

  const handleParticipantJoined = useCallback((participant: Participant) => {
    if (webrtcRef.current && joined) {
      webrtcRef.current.connectToPeer(participant.id, true);
    }
  }, [joined]);

  const handleParticipantLeft = useCallback((participantId: string) => {
    webrtcRef.current?.removePeer(participantId);
    setRemoteStreams((prev) => {
      const next = new Map(prev);
      next.delete(participantId);
      return next;
    });
  }, []);

  const meetingHook = useMeeting(
    publicId,
    joined,
    user?.id,
    user?.name,
    handleSignal,
    handleParticipantJoined,
    handleParticipantLeft
  );

  // Initialize media devices on mount for preview
  useEffect(() => {
    devices.startMedia(true, true);
    return () => {
      devices.stopMedia();
    };
  }, []);

  // Update WebRTC participant ID when assigned by DO
  useEffect(() => {
    if (meetingHook.myParticipantId && webrtcRef.current) {
      webrtcRef.current.setMyParticipantId(meetingHook.myParticipantId);
    }
  }, [meetingHook.myParticipantId]);

  // Connect to peers already present in the room when joined
  useEffect(() => {
    if (!joined || !webrtcRef.current) return;
    for (const p of meetingHook.participants) {
      if (p.id !== meetingHook.myParticipantId && p.userId !== user?.id) {
        webrtcRef.current.connectToPeer(p.id, false);
      }
    }
  }, [joined, meetingHook.participants, meetingHook.myParticipantId, user?.id]);

  // Update local stream on active WebRTC peer connections
  useEffect(() => {
    if (webrtcRef.current && devices.localStream) {
      webrtcRef.current.updateLocalStream(devices.localStream);
    }
  }, [devices.localStream]);

  // Listen for host remote mute
  useEffect(() => {
    const handleHostMute = () => {
      devices.toggleAudio();
    };
    window.addEventListener('cfmeet:host-mute', handleHostMute);
    return () => window.removeEventListener('cfmeet:host-mute', handleHostMute);
  }, [devices]);

  // Audio level monitoring to broadcast speaking state
  useEffect(() => {
    if (!joined || !devices.localStream) return;

    audioMonitorRef.current = new AudioLevelMonitor((isSpeaking) => {
      meetingHook.sendStateUpdate({ speaking: isSpeaking });
    });
    audioMonitorRef.current.start(devices.localStream);

    return () => {
      audioMonitorRef.current?.stop();
    };
  }, [joined, devices.localStream, meetingHook.sendStateUpdate]);

  // Handle joining meeting from lobby
  const handleJoin = async (password?: string, guestName?: string) => {
    const res = await api.joinMeeting(publicId, password, guestName);
    if (res.user && !user) {
      setUser(res.user);
    }

    // Initialize WebRTC Manager with Cloudflare TURN & SFU
    const rtc = new WebRTCManager(
      publicId,
      handleRemoteTrack,
      meetingHook.myParticipantId || undefined
    );
    rtc.setSendSignal(meetingHook.sendSignal);
    await rtc.init(devices.localStream || undefined);
    rtc.connectSfu();
    webrtcRef.current = rtc;

    setJoined(true);
  };

  const handleLeave = () => {
    devices.stopMedia();
    webrtcRef.current?.close();
    setScreenStream(null);
    setRemoteStreams(new Map());
    api.leaveMeeting(publicId).catch(() => {});
    if (user && !user.id.startsWith('guest_')) {
      window.location.hash = '#/dashboard';
    } else {
      window.location.hash = '#/login';
    }
  };

  const handleEndMeeting = async () => {
    devices.stopMedia();
    webrtcRef.current?.close();
    setScreenStream(null);
    setRemoteStreams(new Map());
    await meetingHook.endMeeting();
    window.location.hash = '#/dashboard';
  };

  const handleToggleScreenShare = async () => {
    if (isScreenSharing) {
      await webrtcRef.current?.stopScreenShare();
      setIsScreenSharing(false);
      setScreenStream(null);
      meetingHook.sendStateUpdate({ screen: false });
    } else {
      const stream = await webrtcRef.current?.shareScreen();
      if (stream) {
        setIsScreenSharing(true);
        setScreenStream(stream);
        meetingHook.sendStateUpdate({ screen: true });
        stream.getVideoTracks()[0].onended = async () => {
          await webrtcRef.current?.stopScreenShare();
          setIsScreenSharing(false);
          setScreenStream(null);
          meetingHook.sendStateUpdate({ screen: false });
        };
      }
    }
  };

  const handleToggleHandRaise = () => {
    const next = !isHandRaised;
    setIsHandRaised(next);
    meetingHook.sendStateUpdate({ handRaised: next });
  };

  const handleToggleAudio = () => {
    devices.toggleAudio();
    meetingHook.sendStateUpdate({ audio: devices.isAudioMuted });
  };

  const handleToggleVideo = () => {
    devices.toggleVideo();
    meetingHook.sendStateUpdate({ video: devices.isVideoOff });
  };

  // Pre-join Lobby View
  if (!joined) {
    return (
      <Lobby
        title={meetingHook.meeting?.title || 'Video Meeting'}
        hasPassword={Boolean(meetingHook.meeting?.hasPassword)}
        isHost={Boolean(meetingHook.meeting?.isHost)}
        isAuthenticated={Boolean(user)}
        initialGuestName={user?.name || ''}
        localStream={devices.localStream}
        isAudioMuted={devices.isAudioMuted}
        isVideoOff={devices.isVideoOff}
        audioDevices={devices.audioDevices}
        videoDevices={devices.videoDevices}
        selectedAudioId={devices.selectedAudioId}
        selectedVideoId={devices.selectedVideoId}
        onSelectAudio={devices.setSelectedAudioId}
        onSelectVideo={devices.setSelectedVideoId}
        onToggleAudio={devices.toggleAudio}
        onToggleVideo={devices.toggleVideo}
        onJoin={handleJoin}
      />
    );
  }

  // Meeting Ended View
  if (meetingHook.isMeetingEnded) {
    return (
      <div className="min-h-screen bg-[#090b10] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-[#121622] border border-slate-800 rounded-3xl p-8 text-center space-y-4 shadow-2xl">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center mx-auto">
            <PhoneOff className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-white">This meeting has ended</h2>
          <p className="text-xs text-slate-400">The host has closed this meeting room.</p>
          <a
            href="#/dashboard"
            className="block w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold transition"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Removed / Kicked View
  if (meetingHook.wasKicked) {
    return (
      <div className="min-h-screen bg-[#090b10] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-[#121622] border border-slate-800 rounded-3xl p-8 text-center space-y-4 shadow-2xl">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center mx-auto">
            <PhoneOff className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-white">Removed from Meeting</h2>
          <p className="text-xs text-slate-400">You have been removed by the meeting host.</p>
          <a
            href="#/dashboard"
            className="block w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  const localParticipant: Participant = {
    id: meetingHook.myParticipantId || 'local',
    userId: user?.id || 'me',
    name: user?.name || 'You',
    role: meetingHook.meeting?.isHost ? 'host' : 'participant',
    audio: !devices.isAudioMuted,
    video: !devices.isVideoOff,
    screen: isScreenSharing,
    handRaised: isHandRaised,
    stream: isScreenSharing && screenStream ? screenStream : devices.localStream,
  };

  const remoteParticipants: Participant[] = meetingHook.participants
    .filter((p) => {
      if (meetingHook.myParticipantId && p.id === meetingHook.myParticipantId) return false;
      if (user?.id && p.userId === user.id) return false;
      return true;
    })
    .map((p) => ({
      ...p,
      stream: remoteStreams.get(p.id) || remoteStreams.get(p.userId) || null,
    }));

  return (
    <div className="fixed inset-0 h-[100dvh] h-screen w-full bg-[#090b10] flex flex-col overflow-hidden select-none">
      <ReconnectingBanner isReconnecting={meetingHook.isReconnecting} />

      {/* Floating Reactions Overlay */}
      <div className="fixed bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex space-x-2">
        {meetingHook.activeReactions.map((r) => (
          <div
            key={r.id}
            className="text-3xl sm:text-4xl animate-bounce bg-slate-900/80 backdrop-blur-md p-2.5 sm:p-3 rounded-full border border-slate-700 shadow-2xl"
          >
            {r.reaction}
          </div>
        ))}
      </div>

      {/* Top Header Bar */}
      <header className="h-12 sm:h-14 border-b border-slate-800/80 bg-[#0c0f17]/95 px-3 sm:px-6 flex items-center justify-between z-10 flex-shrink-0">
        <div className="flex items-center space-x-2 sm:space-x-3 truncate">
          <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 flex-shrink-0">
            <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2 truncate">
            <h1 className="text-xs sm:text-sm font-semibold text-white truncate max-w-[130px] sm:max-w-md">
              {meetingHook.meeting?.title || 'Cloudflare Meet'}
            </h1>
            <span className="text-[10px] font-mono text-slate-500 hidden md:inline">
              ({publicId})
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2 text-xs text-slate-400">
          <span className="flex items-center space-x-1 text-emerald-400 font-medium text-[11px] sm:text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="hidden sm:inline">Cloudflare SFU Active</span>
            <span className="sm:hidden font-semibold">SFU</span>
          </span>
        </div>
      </header>

      {/* Main Call Workspace: Video Stage & Overlay Side Panels */}
      <div className="flex-1 min-h-0 w-full overflow-hidden relative">
        <VideoGrid
          localParticipant={localParticipant}
          localStream={isScreenSharing && screenStream ? screenStream : devices.localStream}
          remoteParticipants={remoteParticipants}
          layoutMode={layoutMode}
          pinnedId={pinnedId}
          onTogglePin={(id) => setPinnedId((prev) => (prev === id ? null : id))}
        />

        {/* Overlay Side Drawer (z-40): Floats OVER the stage so the main UI does NOT shrink */}
        {activePanel && (
          <div className="absolute inset-y-0 right-0 z-40 w-full sm:w-80 md:w-96 shadow-2xl flex flex-col bg-[#121622]/95 backdrop-blur-xl border-l border-slate-800 animate-in slide-in-from-right duration-200">
            {activePanel === 'participants' && (
              <ParticipantList
                participants={[localParticipant, ...remoteParticipants]}
                currentUserId={user?.id || ''}
                isHost={Boolean(meetingHook.meeting?.isHost)}
                onClose={() => setActivePanel(null)}
                onMuteParticipant={meetingHook.muteParticipant}
                onRemoveParticipant={meetingHook.kickParticipant}
                onMuteAll={meetingHook.muteAll}
              />
            )}

            {activePanel === 'chat' && (
              <ChatPanel
                messages={meetingHook.chatMessages}
                currentUserId={user?.id || ''}
                onSendMessage={meetingHook.sendChat}
                onClose={() => setActivePanel(null)}
              />
            )}

            {activePanel === 'files' && (
              <FileShareModal
                publicId={publicId}
                onClose={() => setActivePanel(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* Floating Draggable Self-View Popup (Zoom & Google Meet Industry Standard) */}
      <SelfVideoPopup
        localParticipant={localParticipant}
        localStream={devices.localStream}
        isAudioMuted={devices.isAudioMuted}
        isVideoOff={devices.isVideoOff}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
      />

      {/* Bottom Control Bar */}
      <ControlBar
        isAudioMuted={devices.isAudioMuted}
        isVideoOff={devices.isVideoOff}
        isScreenSharing={isScreenSharing}
        isHandRaised={isHandRaised}
        layoutMode={layoutMode}
        participantCount={remoteParticipants.length + 1}
        unreadCount={0}
        isHost={Boolean(meetingHook.meeting?.isHost)}
        activePanel={activePanel}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onToggleScreenShare={handleToggleScreenShare}
        onToggleHandRaise={handleToggleHandRaise}
        onSendReaction={meetingHook.sendReaction}
        onToggleLayout={() => setLayoutMode(layoutMode === 'grid' ? 'speaker' : 'grid')}
        onTogglePanel={(panel) => setActivePanel(activePanel === panel ? null : panel)}
        onOpenSettings={() => setShowSettings(true)}
        onLeave={handleLeave}
        onEndMeeting={meetingHook.meeting?.isHost ? handleEndMeeting : undefined}
      />

      {/* Device Settings Modal */}
      {showSettings && (
        <DeviceSelector
          audioDevices={devices.audioDevices}
          videoDevices={devices.videoDevices}
          selectedAudioId={devices.selectedAudioId}
          selectedVideoId={devices.selectedVideoId}
          onSelectAudio={devices.setSelectedAudioId}
          onSelectVideo={devices.setSelectedVideoId}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};
